import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  recommendationValidationApi,
  type BenchmarkEntry,
  type ConfidenceEvolution,
  type FailureClassification,
  type LearningUpdate,
  type OutcomeMeasurement,
  type PatternEntry,
  type ValidatedRecommendation,
  type ValidationDashboardSnapshot,
  type ValidationTimelineEvent,
} from '../../api/recommendation-validation-val001-runtime';

type Tab = 'dashboard' | 'recommendations' | 'patterns' | 'benchmarks' | 'failures' | 'learning' | 'timeline';

export function RecommendationValidationPage() {
  const [tab, setTab] = useState<Tab>('dashboard');
  const [dashboard, setDashboard] = useState<ValidationDashboardSnapshot | null>(null);
  const [recommendations, setRecommendations] = useState<ValidatedRecommendation[]>([]);
  const [patterns, setPatterns] = useState<PatternEntry[]>([]);
  const [benchmarks, setBenchmarks] = useState<BenchmarkEntry[]>([]);
  const [failures, setFailures] = useState<FailureClassification[]>([]);
  const [learning, setLearning] = useState<LearningUpdate[]>([]);
  const [timeline, setTimeline] = useState<ValidationTimelineEvent[]>([]);
  const [selected, setSelected] = useState<ValidatedRecommendation | null>(null);
  const [outcomes, setOutcomes] = useState<OutcomeMeasurement | null>(null);
  const [confidence, setConfidence] = useState<ConfidenceEvolution | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const card = 'border-violet-200 bg-white dark:border-violet-900/40 dark:bg-zinc-900';
  const muted = 'text-slate-500 dark:text-zinc-400';
  const accent = 'text-violet-700 dark:text-violet-300';
  const shell = 'bg-violet-50/20 dark:bg-zinc-950';

  const refresh = useCallback(async () => {
    setError(null); setLoading(true);
    try {
      const [dash, recs, pat, bench, fail, learn, tl] = await Promise.all([
        recommendationValidationApi.dashboard(), recommendationValidationApi.recommendations(),
        recommendationValidationApi.patterns(), recommendationValidationApi.benchmarks(),
        recommendationValidationApi.failures(), recommendationValidationApi.learning(),
        recommendationValidationApi.history(),
      ]);
      setDashboard(dash); setRecommendations(recs); setPatterns(pat); setBenchmarks(bench);
      setFailures(fail); setLearning(learn); setTimeline(tl);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load validation platform');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const inspect = async (rec: ValidatedRecommendation) => {
    setSelected(rec);
    try {
      const [out, conf] = await Promise.all([
        recommendationValidationApi.outcomes(rec.id), recommendationValidationApi.confidence(rec.id),
      ]);
      setOutcomes(out); setConfidence(conf); setTab('recommendations');
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load recommendation details'); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'dashboard', label: 'Overview' }, { id: 'recommendations', label: 'Recommendations' },
    { id: 'patterns', label: 'Patterns' }, { id: 'benchmarks', label: 'Benchmarks' },
    { id: 'failures', label: 'Failures' }, { id: 'learning', label: 'Learning' }, { id: 'timeline', label: 'Timeline' },
  ];

  return (
    <div className={`space-y-4 rounded-xl p-1 ${shell}`} role="main">
      <header className={`rounded-xl border p-4 ${card}`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>VAL-000001 · Scientific Recommendation Validation</p>
            <h1 className="text-xl font-semibold">Recommendation Validation Platform</h1>
            {dashboard && (
              <div className={`mt-2 flex flex-wrap gap-x-4 text-sm ${muted}`}>
                <span>Success rate <strong className={accent}>{dashboard.success_rate_pct}%</strong></span>
                <span>Accuracy <strong className={accent}>{dashboard.accuracy_pct}%</strong></span>
                <span>Validated <strong className={accent}>{dashboard.total_validated}</strong></span>
                <span>Impact <strong className={accent}>{dashboard.business_impact_usd}</strong></span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Link to="/marketplace-brain" className={`rounded-lg border px-3 py-1.5 text-sm ${card} ${accent}`}>Operating Brain</Link>
            <Link to="/platform/recommendation-validation-val001" className={`rounded-lg border px-3 py-1.5 text-sm ${card} ${accent}`}>Sprint Center</Link>
          </div>
        </div>
      </header>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {loading && <p className={`text-sm ${muted}`}>Loading validation data…</p>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? 'bg-violet-600 text-white' : `${card} border`}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'dashboard' && dashboard && (
        <div className="grid gap-3 sm:grid-cols-4">
          {[
            { label: 'Success Rate', value: `${dashboard.success_rate_pct}%` },
            { label: 'Accuracy', value: `${dashboard.accuracy_pct}%` },
            { label: 'Learning Velocity', value: dashboard.learning_velocity },
            { label: 'Confidence Trend', value: dashboard.confidence_trend },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
              <p className="text-2xl font-semibold capitalize">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-4">
          {recommendations.map((rec) => (
            <button key={rec.id} type="button" onClick={() => inspect(rec)} className={`block w-full rounded-xl border p-4 text-left ${card} ${selected?.id === rec.id ? 'ring-2 ring-violet-400' : ''}`}>
              <div className="flex justify-between"><p className="font-semibold">{rec.title}</p><span className={`text-sm ${accent}`}>{Math.round(rec.success_score * 100)}% score</span></div>
              <p className={`text-xs ${muted}`}>{rec.source} · {rec.lifecycle_stage} · confidence {Math.round(rec.current_confidence * 100)}%</p>
              {rec.business_impact && <p className={`mt-1 text-sm ${accent}`}>{rec.business_impact}</p>}
            </button>
          ))}
          {selected && outcomes && confidence && (
            <div className={`rounded-xl border p-4 ${card}`}>
              <h3 className="font-semibold">Outcome & Confidence — {selected.title}</h3>
              <p className={`mt-2 text-sm ${muted}`}>Measured {outcomes.measured_impact} · Estimated {outcomes.estimated_impact} · Predicted {outcomes.predicted_impact}</p>
              <p className={`text-sm ${muted}`}>Confidence {Math.round(confidence.initial_confidence * 100)}% → {Math.round(confidence.current_confidence * 100)}% ({confidence.trend}) · {confidence.evidence_count} evidence items</p>
            </div>
          )}
        </div>
      )}

      {tab === 'patterns' && patterns.map((p) => (
        <div key={p.id} className={`rounded-xl border p-4 ${card}`}>
          <div className="flex justify-between"><p className="font-semibold">{p.name}</p><span className={`text-sm ${p.type === 'winning' ? 'text-violet-600' : 'text-red-600'}`}>{p.win_rate_pct}% win</span></div>
          <p className={`text-xs ${muted}`}>{p.type} · {p.category} · n={p.sample_size}</p>
        </div>
      ))}

      {tab === 'benchmarks' && benchmarks.map((b) => (
        <div key={b.id} className={`rounded-xl border p-4 ${card}`}>
          <p className="font-semibold">{b.dimension}</p>
          <p className={`text-sm ${accent}`}>{b.value} vs {b.benchmark} — {b.percentile}th percentile</p>
        </div>
      ))}

      {tab === 'failures' && failures.map((f) => (
        <div key={f.id} className={`rounded-xl border p-4 ${card}`}>
          <p className="font-semibold">{f.title}</p>
          <p className={`text-xs ${muted}`}>{f.classification}{f.recoverable ? ' · recoverable' : ''}</p>
        </div>
      ))}

      {tab === 'learning' && learning.map((u) => (
        <div key={u.id} className={`rounded-xl border p-4 ${card}`}>
          <p className="font-semibold">{u.action}</p>
          <p className={`text-xs ${muted}`}>{u.target} · {u.impact}</p>
        </div>
      ))}

      {tab === 'timeline' && timeline.map((e) => (
        <div key={e.id} className={`rounded-xl border p-4 ${card}`}>
          <p className="font-semibold">{e.title}</p>
          <p className={`text-xs ${muted}`}>{new Date(e.timestamp).toLocaleString()}</p>
        </div>
      ))}
    </div>
  );
}
